import { Controller as BaseController } from 'stimulus'
import { useBusItem } from '@app/javascript/controllers/mixins/useBusItem.js'
import { getCurrentLocale } from '@app/javascript/locales'
import I18n from '@app/javascript/i18n/index.js.erb'
import min from 'lodash.min'
import castArray from 'lodash.castarray'

export class Controller extends BaseController {
  static targets = ['exportButton', 'exportLimitHint']

  static values = {
    exportRequestUrl: String,
    exportRequestHttpVerb: String,
    currentSearchParams: Object,
    exportLimit: Number,
    exportLimitFormatted: String,
    exportOrigin: String,
    totalCount: Number,
    supportedContentTypes: Array,
    supportedStakeholderTypes: Array,
    buttonHintCopy: String
  }

  connect() {
    useBusItem(this, { addToBus: false })

    this.exportButtonLabel = this.exportButtonTarget.querySelector('.button-label')
    this.selectedEntityIds = []
  }

  onSearchChanged(event) {
    this.isResultsLoaded = false
    this.totalCountValue = 0
    this.currentSearchParamsValue = event.detail.paramsObj
    this.setDisabled()
  }

  onResultsLoaded(event) {
    this.isResultsLoaded = true
    this.totalCountValue = event.detail.totalCount
    this.setEnabledDisabled()
  }

  onResultsSelected(event) {
    if (!this.isResultsLoaded) return

    const { selectedEntityIds } = event.detail

    this.selectedEntityIds = selectedEntityIds
    this.setEnabledDisabled()
  }

  setEnabledDisabled() {
    const isDisabled = this.totalToExport === 0 || !this.isExportSupportedForCurrentSearch()

    isDisabled ? this.setDisabled() : this.setEnabled()
  }

  setEnabled() {
    if (this.totalToExport <= this.exportLimitValue) {
      this.setEnabledBelowLimit()
    } else {
      this.setEnabledBeyondLimit()
    }
  }

  setEnabledBelowLimit() {
    const formattedCount = new Intl.NumberFormat(getCurrentLocale()).format(this.totalToExport)

    this.exportButtonLabel.innerHTML = I18n.t('search_v2.export_results.button_label_enabled', { count: formattedCount })
    this.exportButtonTarget.disabled = false

    this.hideExportLimitHint()
  }

  setEnabledBeyondLimit() {
    this.exportButtonLabel.innerHTML = I18n.t('search_v2.export_results.button_label_limit', { export_limit: this.exportLimitFormattedValue })
    this.exportButtonTarget.disabled = false

    this.updateExportLimitHint(this.buttonHintCopyValue, { export_limit: this.exportLimitFormattedValue })
  }

  setDisabled() {
    this.exportButtonLabel.innerHTML = I18n.t('search_v2.export_results.button_label_disabled')
    this.exportButtonTarget.disabled = true

    if (this.totalToExport === 0) {
      this.hideExportLimitHint()
    } else {
      this.updateExportLimitHint('search_v2.export_results.button_hint_disabled')
    }
  }

  updateExportLimitHint(copyString, stringOptions = {}) {
    if (!this.hasExportLimitHintTarget) return

    const hintTargetController = this.exportLimitHintTarget.controllers['vendor--tippy']

    if (hintTargetController) {
      hintTargetController.optionsValue = { content: I18n.t(copyString, stringOptions) }
      hintTargetController.reset()
    }

    this.exportLimitHintTarget.classList.remove('hidden')
  }

  hideExportLimitHint() {
    if (!this.hasExportLimitHintTarget) return

    this.exportLimitHintTarget.classList.add('hidden')
  }

  setExportFormAttributes() {
    const exportController = this.getBusItem('export-generator')
    exportController.savePathValue = this.exportRequestUrlValue
    exportController.saveHttpVerbValue = this.exportRequestHttpVerbValue
    exportController.exportOriginValue = this.exportOriginValue
    exportController.entityIdsValue = []
    exportController.totalValue = min([this.totalToExport, this.exportLimitValue])
    exportController.searchAttributesValue = $.param({
      search_params: this.currentSearchParamsValue,
      selected_entity_ids: this.selectedEntityIds
    })
  }

  isExportSupportedForCurrentSearch() {
    if (!this.isContentTypeSupported()) { return false }
    if (!this.isSelectedEntityTypeSupported()) { return false }
    if (!this.isSelectedStakeholderTypeSupported()) { return false }

    return true
  }

  isContentTypeSupported() {
    if (this.supportedContentTypesValue.length === 0 || !this.currentSearchParamsValue.content_type) { return true }

    return castArray(this.currentSearchParamsValue.content_type).every(value => this.supportedContentTypesValue.includes(value))
  }

  isSelectedStakeholderTypeSupported() {
    if (this.supportedStakeholderTypesValue.length === 0 || !this.currentSearchParamsValue.stakeholder_type) { return true }

    return castArray(this.currentSearchParamsValue.stakeholder_type).some(value => this.supportedStakeholderTypesValue.includes(value))
  }

  isSelectedEntityTypeSupported() {
    return this.getBusItem('entity-select').supportsSelectedEntityIds(this.exportButtonTarget)
  }

  get totalToExport() {
    const hasSelectedEntities = this.selectedEntityIds && this.selectedEntityIds.length > 0

    return hasSelectedEntities ? this.selectedEntityIds.length : this.totalCountValue
  }
}
